/* eslint-disable prettier/prettier */
import React, { useEffect, useState } from 'react';
import styles from '../../../styles/components/pageComponents/program/EditProgramInfo.module.css';
import SearchableDropdown from '../../controlVariations/SearchableDropdown';
import { useUploadCover } from '../../../hooks/useCourse';
import { useSolutions } from '../../../hooks/useCourse';
import { useUpdateCourse } from '../../../hooks/useUpdateCourse';
import ResponsiveModal from '../../../components/controlVariations/ResponsiveModal';
import SuccessContent from '../../../components/controlVariations/ModularSuccessModal';
import AddSolution from './AddSolution';
import { apiService } from '../../../services/api.service';

interface EditProgramInfoProps {
	onClose: () => void; // Renamed from onBack to better suit a popup
	course: any;
	onSaveSuccess?: () => void;
}

const EditProgramInfo: React.FC<EditProgramInfoProps> = ({
	onClose,
	course,
	onSaveSuccess,
}) => {
	const [formData, setFormData] = useState<Record<string, string>>({
		title: '',
		description: '',
		solution: '',
	});

	const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);

	useEffect(() => {
		if (course) {
			setFormData({
				title: course.title || '',
				description: course.description || '',
				solution: course.solutionid || '',
			});

			// if (course.coverimage) {
			//     setCoverPreview(course.coverimage);
			// }
		}
	}, [course]);

	useEffect(() => {
		const fetchCover = async () => {
			try {
				const res = await apiService.get<{ picture: string }>(
					`course/${course.id}/cover`
				);

				setCoverPreview(res?.data?.picture);
			} catch (err) {
				console.warn('Cover fetch failed');
			}
		};

		if (course?.id) {
			fetchCover();
		}
	}, [course?.id]);

	// Cover photo state
	const [coverFile, setCoverFile] = useState<File | null>(null);
	const [coverPreview, setCoverPreview] = useState<string | null>(null);

	const [successModal, setSuccessModal] = useState<{
		open: boolean;
		title: string;
		subtitle: string;
	}>({
		open: false,
		title: '',
		subtitle: '',
	});

	const showSuccess = (title: string, subtitle: string) => {
		setSuccessModal({
			open: true,
			title,
			subtitle,
		});

		setTimeout(() => {
			setSuccessModal(prev => ({ ...prev, open: false }));
		}, 3000);
	};
	// Errors state
	const [errors, setErrors] = useState<Record<string, string>>({});

	// API Hooks
	const { solutions, createSolution } = useSolutions();
	const { updateCourse, loading } = useUpdateCourse();
	const { uploadCover } = useUploadCover();

	const validateForm = () => {
		const newErrors: Record<string, string> = {};
		if (!formData['solution'])
			newErrors.solution = 'Offering category is required';
		if (!formData['title']) newErrors.title = 'Program title is required';

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleCoverSelect = (file: File) => {
		setCoverFile(file);
		const previewUrl = URL.createObjectURL(file);
		setCoverPreview(previewUrl);
	};

	const handleInputChange = (id: string, value: string) => {
		setFormData(prev => ({ ...prev, [id]: value }));
		// Clear error when user types
		if (errors[id]) {
			setErrors(prev => ({ ...prev, [id]: '' }));
		}
	};

	const handleAddSolution = async (title: string) => {
		try {
			const newSolution = await createSolution({ title });

			// 🔥 Refresh list

			setFormData(prev => ({
				...prev,
				solution: newSolution.id,
			}));

			setIsAddSolutionOpen(false);
		} catch (err) {
			console.error(err);
		}
	};

	const handleSaveChanges = async () => {
		if (!validateForm()) return;

		try {
			console.log('🚀 Updating course:', course.id);
			console.log('📸 coverFile:', coverFile);

			const updated = await updateCourse(course.id, {
				title: formData['title'],
				description: formData['description'],
				solutionid: formData['solution'],
			});

			console.log('✅ updateCourse response:', updated);

			if (coverFile) {
				console.log('📤 Uploading cover...');
				await uploadCover(course.id, coverFile);
				console.log('✅ Cover uploaded');
			} else {
				console.log('⚠️ No cover file selected');
			}

			// if (!updated) return;

			// // Upload cover if changed
			// if (coverFile) {
			//     await uploadCover(course.id, coverFile);
			// }

			onSaveSuccess?.();

			onClose();
		} catch (err) {
			console.error(err);
			console.error('❌ Error in save:', err);
		}
	};

	const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010';
	return (
		<div className={styles.modalOverlay}>
			<div className={styles.modalContainer}>
				{/* Header */}
				<div className={styles.modalHeader}>
					<h2 className={styles.modalTitle}>
						Update Program Details – Program Info
					</h2>
					<div className={styles.headerActions}>
						<button
							className={styles.btnCancel}
							onClick={onClose}
							disabled={loading}
						>
							Cancel
						</button>
						<button
							className={styles.btnSave}
							onClick={handleSaveChanges}
							disabled={loading}
						>
							{loading ? 'Saving...' : 'Save Changes'}
						</button>
					</div>
				</div>

				{/* Form Body */}
				<div className={styles.modalBody}>
					<div className={styles.topGrid}>
						{/* Left Column: Dropdown & Title */}
						<div className={styles.leftCol}>
							{/* Offering Category */}
							<div className={styles.fieldWrapper}>
								<div className={styles.labelRow}>
									<label className={styles.label}>
										Offering Category
									</label>
									<button
										type="button"
										className={styles.addLink}
										onClick={() =>
											setIsAddSolutionOpen(true)
										}
									>
										Add New Offering +
									</button>
								</div>
								<SearchableDropdown
									placeholder="Coach Education"
									options={solutions.map(sol => sol.title)}
									selected={
										solutions.find(
											sol =>
												sol.id === formData['solution']
										)?.title
									}
									onSelect={value => {
										const selectedSol = solutions.find(
											sol => sol.title === value
										);
										if (selectedSol)
											handleInputChange(
												'solution',
												selectedSol.id
											);
									}}
								/>
								{errors.solution && (
									<span className={styles.errorText}>
										{errors.solution}
									</span>
								)}
							</div>

							{/* Program Title */}
							<div className={styles.fieldWrapper}>
								<div className={styles.labelRow}>
									<label className={styles.label}>
										Program Title
									</label>
									<span className={styles.counter}>
										{(formData['title'] || '').length}/60
									</span>
								</div>
								<input
									type="text"
									className={`${styles.input} ${formData['title'] ? styles.inputActive : ''}`}
									placeholder="Program Title"
									maxLength={60}
									value={formData['title'] || ''}
									onChange={e =>
										handleInputChange(
											'title',
											e.target.value
										)
									}
								/>
								{errors.title && (
									<span className={styles.errorText}>
										{errors.title}
									</span>
								)}
							</div>
						</div>

						{/* Right Column: Cover Photo */}
						<div className={styles.rightCol}>
							<div
								className={styles.photoBox}
								onClick={() =>
									document
										.getElementById('coverInput')
										?.click()
								}
							>
								{coverPreview ? (
									<img
										src={coverPreview}
										alt="cover"
										className={styles.coverPreview}
									/>
								) : (
									<button
										type="button"
										className={styles.uploadBtn}
									>
										Add Cover Photo
										<svg
											width="16"
											height="16"
											viewBox="0 0 24 24"
											fill="none"
											stroke="currentColor"
											strokeWidth="2"
											strokeLinecap="round"
											strokeLinejoin="round"
											style={{ marginLeft: '8px' }}
										>
											<rect
												x="3"
												y="3"
												width="18"
												height="18"
												rx="2"
												ry="2"
											></rect>
											<circle
												cx="8.5"
												cy="8.5"
												r="1.5"
											></circle>
											<polyline points="21 15 16 10 5 21"></polyline>
										</svg>
									</button>
								)}

								<input
									id="coverInput"
									type="file"
									accept="image/*"
									style={{ display: 'none' }}
									onChange={e => {
										const file = e.target.files?.[0];
										if (file) handleCoverSelect(file);
									}}
								/>
							</div>
						</div>
					</div>

					{/* Bottom Row: Description */}
					<div
						className={`${styles.fieldWrapper} ${styles.fullWidth}`}
					>
						<div className={styles.labelRow}>
							<label className={styles.label}>Description</label>
							<span className={styles.counter}>
								{(formData['description'] || '').length}/500
							</span>
						</div>
						<textarea
							className={styles.textarea}
							placeholder="Program details (Optional)"
							maxLength={500}
							value={formData['description'] || ''}
							onChange={e =>
								handleInputChange('description', e.target.value)
							}
						/>
					</div>
				</div>
			</div>

			<ResponsiveModal
				open={successModal.open}
				onClose={() =>
					setSuccessModal(prev => ({ ...prev, open: false }))
				}
			>
				<SuccessContent
					title={successModal.title}
					subtitle={successModal.subtitle}
				/>
			</ResponsiveModal>

			<ResponsiveModal
				open={isAddSolutionOpen}
				onClose={() => setIsAddSolutionOpen(false)}
			>
				<AddSolution
					onOk={handleAddSolution}
					onCancel={() => setIsAddSolutionOpen(false)}
				/>
			</ResponsiveModal>
		</div>
	);
};

export default EditProgramInfo;
